// PWA detection and utilities
export const isPWA = () => {
  // Check if running in standalone mode (PWA)
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  
  // Check for iOS standalone mode
  if (window.navigator.standalone === true) {
    return true;
  }
  
  // Check if launched from home screen
  if (window.matchMedia('(display-mode: fullscreen)').matches) {
    return true;
  }
  
  // Check for minimal-ui mode (PWA)
  if (window.matchMedia('(display-mode: minimal-ui)').matches) {
    return true;
  }
  
  // Only consider it PWA if it's actually installed and running in standalone mode
  // Don't rely on service worker or HTTPS alone as these can be present in regular browsers
  return false;
};

export const isMobilePWA = () => {
  return isPWA() && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

export const getPWADisplayMode = () => {
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return 'standalone';
  }
  if (window.matchMedia('(display-mode: fullscreen)').matches) {
    return 'fullscreen';
  }
  if (window.matchMedia('(display-mode: minimal-ui)').matches) {
    return 'minimal-ui';
  }
  return 'browser';
};

export const setupPWAFeatures = () => {
  // Add PWA-specific CSS classes
  if (isPWA()) {
    document.body.classList.add('pwa-mode');
    document.body.classList.add(`pwa-${getPWADisplayMode()}`);
    
    if (isMobilePWA()) {
      document.body.classList.add('pwa-mobile');
    }
  }
  
  // Handle PWA-specific viewport settings
  if (isPWA()) {
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute('content', 
        'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover'
      );
    }
  }
  
  return {
    isPWA: isPWA(),
    isMobilePWA: isMobilePWA(),
    displayMode: getPWADisplayMode()
  };
};

export const addPWAStyles = () => {
  // Add PWA-specific styles dynamically
  const style = document.createElement('style');
  style.textContent = `
    .pwa-mode {
      --pwa-safe-area-top: env(safe-area-inset-top);
      --pwa-safe-area-bottom: env(safe-area-inset-bottom);
      --pwa-safe-area-left: env(safe-area-inset-left);
      --pwa-safe-area-right: env(safe-area-inset-right);
    }
    
    .pwa-mobile .main-wrapper {
      padding-top: var(--pwa-safe-area-top, 0);
      padding-bottom: var(--pwa-safe-area-bottom, 0);
    }
    
    .pwa-mode .pwa-header {
      padding-top: var(--pwa-safe-area-top, 0);
    }
    
    .pwa-mode .pwa-content {
      padding-bottom: var(--pwa-safe-area-bottom, 0);
    }
    
    .pwa-mode .pwa-fab {
      bottom: calc(20px + var(--pwa-safe-area-bottom, 0));
    }
    
    @media (display-mode: standalone) {
      .pwa-mode .navbar {
        padding-top: var(--pwa-safe-area-top, 0);
      }
    }
  `;
  document.head.appendChild(style);
};

export const handlePWAGestures = () => {
  if (!isPWA()) return;
  
  let startY = 0;
  let currentY = 0;
  let isPulling = false;
  
  const handleTouchStart = (e) => {
    startY = e.touches[0].clientY;
  };
  
  const handleTouchMove = (e) => {
    currentY = e.touches[0].clientY;
    const pullDistance = currentY - startY;
    
    if (pullDistance > 50 && window.scrollY === 0) {
      isPulling = true;
      // Add pull-to-refresh visual feedback
      document.body.style.transform = `translateY(${Math.min(pullDistance * 0.3, 50)}px)`;
    }
  };
  
  const handleTouchEnd = () => {
    if (isPulling) {
      document.body.style.transform = '';
      // Trigger refresh
      window.location.reload();
      isPulling = false;
    }
  };
  
  document.addEventListener('touchstart', handleTouchStart, { passive: true });
  document.addEventListener('touchmove', handleTouchMove, { passive: true });
  document.addEventListener('touchend', handleTouchEnd, { passive: true });
};

export const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered successfully:', registration);
      
      // Handle updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New content is available, prompt user to refresh
            if (window.confirm('New version available! Refresh to update?')) {
              window.location.reload();
            }
          }
        });
      });
      
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return null;
    }
  }
  return null;
};

export const requestNotificationPermission = async () => {
  if ('Notification' in window) {
    const permission = await Notification.requestPermission();
    console.log('Notification permission:', permission);
    return permission === 'granted';
  }
  return false;
};

export const showNotification = (title, options = {}) => {
  if ('Notification' in window && Notification.permission === 'granted') {
    const notification = new Notification(title, {
      icon: '/android-chrome-192x192.png',
      badge: '/android-chrome-192x192.png',
      ...options
    });
    
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
    
    return notification;
  }
  return null;
};

export const initializePWA = async () => {
  const pwaInfo = setupPWAFeatures();
  addPWAStyles();
  
  // Register service worker
  await registerServiceWorker();
  
  // Request notification permission
  await requestNotificationPermission();
  
  if (pwaInfo.isPWA) {
    handlePWAGestures();
  }
  
  return pwaInfo;
};
